import { useMemo } from 'react';
import heroEvar from '../assets/hero-evar.jpg';
import ProductCard from '../components/ProductCard/ProductCard';
import Reels from '../components/Reels/Reels';
import { useCatalog } from '../data/useCatalog';
import './Home.css';

const TRUST = [
  { title: 'Cash on delivery', text: 'Pay the courier when the box is in your hands.', icon: 'cash' },
  { title: 'Ships in 72h', text: 'Every governorate, tracked until it arrives.', icon: 'delivery' },
  { title: '7-day exchange', text: 'Wrong size? Swap it, no questions asked.', icon: 'exchange' },
];

function matches(product, filter) {
  if (filter === 'all') return true;
  return product.category === filter;
}

function scrollToShop() {
  document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Star() {
  return (
    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
      <path
        d="M10 2.4l2.3 4.9 5.4.7-3.9 3.7 1 5.3-4.8-2.7-4.8 2.7 1-5.3L2.3 8l5.4-.7z"
        fill="var(--color-accent)"
      />
    </svg>
  );
}

function TrustIcon({ type }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'cash') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><g {...common}><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M7 15h3" /><circle cx="16.5" cy="14" r="2" /></g></svg>;
  }
  if (type === 'delivery') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><g {...common}><path d="M2.5 5.5h12v12h-12zM14.5 9h4l3 3.5v5h-7z" /><circle cx="7" cy="18.5" r="2" /><circle cx="18" cy="18.5" r="2" /><path d="M16 12h3" /></g></svg>;
  }
  if (type === 'exchange') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><g {...common}><path d="M4 8h14l-3-3M20 16H6l3 3" /><path d="M18 8a7 7 0 0 1 2 5M6 16a7 7 0 0 1-2-5" /></g></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><g {...common}><path d="m7 20 13-13-3-3L4 17v3zM14 7l3 3M10 11l2 2M7 14l2 2" /></g></svg>;
}

function Home({ filter, searchQuery = '', onFilterChange }) {
  const { products, reviews, categories } = useCatalog();
  const filters = useMemo(
    () => [{ id: 'all', label: 'All' }, ...categories.map((category) => ({ id: category.id, label: category.title }))],
    [categories]
  );
  const categoryTitles = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category.title])),
    [categories]
  );
  const searched = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    if (!query) return products;
    return products.filter((product) => {
      const category = categoryTitles[product.category];
      return [product.name, product.category, category]
        .filter(Boolean)
        .some((value) => value.toLocaleLowerCase().includes(query));
    });
  }, [products, searchQuery, categoryTitles]);
  const visible = useMemo(
    () => searched.filter((product) => matches(product, filter)),
    [searched, filter]
  );
  const counts = useMemo(
    () =>
      filters.reduce((all, item) => {
        all[item.id] = item.id === 'all' ? searched.length : searched.filter((product) => product.category === item.id).length;
        return all;
      }, {}),
    [searched, filters]
  );

  function pickFilter(id) {
    onFilterChange(id);
    scrollToShop();
  }

  return (
    <main className="home" id="top">
      <section className="hero">
        <div className="hero__content">
          <h1 className="hero__title">
            Choose
            <br />
            Excellence,
            <br />
            Choose EVAR
          </h1>
          <p className="hero__lead">
            Premium clothing. Every detail matters — carefully selected pieces, timeless style, and quality you can feel. Discover your next favorite fit with EVAR.
          </p>
          <div className="hero__cta">
            <button type="button" className="btn" onClick={() => pickFilter('all')}>
              Explore the collection
            </button>
          </div>
        </div>

        <div className="hero__visual">
          <img className="hero__image" src={heroEvar} alt="EVAR looks: jackets, tees and pants" />
        </div>
      </section>

      <Reels />

      <section className="section" id="shop">
        <div className="shop__head">
          <h2 className="section__title">Products</h2>
          <p className="shop__count">{visible.length} pieces</p>
        </div>

        <div className="chips" role="group" aria-label="Filter products">
          {filters.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`chip${filter === item.id ? ' chip--active' : ''}`}
              aria-pressed={filter === item.id}
              onClick={() => onFilterChange(item.id)}
            >
              {item.label}
              <span className="chip__count">[{counts[item.id]}]</span>
            </button>
          ))}
        </div>

        <div className="product-grid">
          {visible.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
        {visible.length === 0 && <p className="shop__empty">No products match your search.</p>}
      </section>

      <section className="section" id="service">
        <div className="trust">
          {TRUST.map((item) => (
            <article className="trust__item" key={item.title}>
              <span className="trust__icon"><TrustIcon type={item.icon} /></span>
              <h3 className="trust__title">{item.title}</h3>
              <p className="trust__text">{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section" id="reviews">
        <h2 className="section__title">What buyers say</h2>
        <div className="reviews" translate="no">
          <div className="reviews__track">
            {[...reviews, ...reviews].map((review, index) => (
              <figure className="review" key={`${review.name}-${index}`} aria-hidden={index >= reviews.length}>
                <div className="review__stars" aria-label="Rated 5 out of 5">
                  {[0, 1, 2, 3, 4].map((star) => (
                    <Star key={star} />
                  ))}
                </div>
                <blockquote className="review__text">{review.text}</blockquote>
                <figcaption className="review__who">
                  {review.name}{' '}
                  <span>&middot; {review.city}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
